import React, { useEffect } from "react";
import { Script, type GetServerDataProps } from "gatsby";

import { ReactifySearchProvider, Sensors } from "@usereactify/search";

import { MetaComponent } from "@bared/ui";
import { CollectionSchema, filterProjection } from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { useTripleWhale } from "~/hooks/useTripleWhale";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { ErrorPage } from "~/components/Error/Error";
import { Collection } from "~/components/Collection/Collection";

import { CustomSensor } from "~/components/CustomSensor";

import { ReviewsProvider } from "~/providers/ReviewsProvider";
import { ProductImageProvider } from "@bared/ui";

const pageQuery = filterProjection(
  CollectionSchema,
  `_type == "collection" && shopify.shopifyHandle == $handle`,
);

const COLLECTION_QUERY = /* GraphQL */ `
  #graphql
  query Collection(
    $country: CountryCode
    $handle: String!
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      id
      handle
      title
      seo {
        title
        description
      }
      image {
        openGraphUrl: url(transform: { maxWidth: 1200 })
      }
      genderMetafield: metafield(
        key: "collection_gender"
        namespace: "custom"
      ) {
        value
      }
      primaryCollection: metafield(
        key: "primary_collection"
        namespace: "custom"
      ) {
        reference {
          ... on Collection {
            id
            title
            handle
          }
        }
      }
    }
  }
` as const;

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, notFound, groq, storefront, site, sectionResolver } =
    context;

  // Resolve handle.
  const handle = props.params?.["handle"];
  if ("string" !== typeof handle) throw new Error("No handle resolved.");

  // Fetch collection from sanity and shopify.
  const [page, shopifyResult] = await Promise.all([
    groq(pageQuery, { handle }),
    storefront.query(COLLECTION_QUERY, { variables: { handle } }),
  ]);

  // Get shopify collection from result.
  const shopifyCollection = shopifyResult.collection;

  // Page not found in Sanity, return 404.
  if (!page || !shopifyCollection) return notFound();

  // Check if this collection is enabled for the site.
  const isEnabled =
    !page.sites || 0 === page.sites.length || page.sites.includes(site.handle);

  // Collection not enabled, return 404.
  if (!isEnabled) return notFound();

  // Get the associated collection gender.
  const collectionGender =
    shopifyResult.collection && shopifyResult.collection.genderMetafield
      ? shopifyResult.collection.genderMetafield.value
      : null;

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    sanityMetadata: page.metadata,
    media: shopifyCollection.image?.openGraphUrl,
    description: shopifyCollection.seo.description,
    title: shopifyCollection.seo.title ?? shopifyCollection.title,
  });

  // Resolve sections in the header.
  page.content = page.content
    ? await sectionResolver.resolve({ sanitySections: page.content })
    : null;

  // Resolve sections in the footer.
  page.footerContent = page.footerContent
    ? await sectionResolver.resolve({ sanitySections: page.footerContent })
    : null;

  // Respond with server data.
  return respond({ page, seo, collectionGender, shopifyCollection } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function CollectionPage(props: PageProps<ServerData>) {
  /**
   * The key prop forces the component to remount when the URI changes rather than just passing
   * through the updated props. We have deep components which do not re-render properly when
   * their props change, so currently remounting is our only option to remain consistent.
   * @see https://bared.atlassian.net/browse/ECOM-2579
   * @see https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1715903163431309
   */
  return <CollectionPageInner {...props} key={props.uri} />;
}

function CollectionPageInner({ serverData, location }: PageProps<ServerData>) {
  const tripleWhale = useTripleWhale();

  // Send tracking events when server data changes.
  useEffect(() => {
    if (200 !== serverData.status) return;

    const { shopifyCollection } = serverData;

    tripleWhale.trackPageLoad("Collection");
  }, [tripleWhale, serverData]);

  const { market, isHydrated } = useRouteLoaderData("routes/$site");

  // Page found, render page.
  if (200 === serverData.status && isHydrated) {
    const { page, collectionGender, shopifyCollection } = serverData;

    return (
      <>
        <ReviewsProvider>
          <ProductImageProvider>
            <ReactifySearchProvider
              mode="collection"
              /** @todo Work out which fields are actually required */
              includeFields={["*"]}
              market={market.legacyId}
              additionalComponentIds={["CustomSensor"]}
              collectionHandle={shopifyCollection.handle}
              shopifyPermanentDomain="bared-footwear-au.myshopify.com"
              theme={{
                colors: {
                  textColor: "rgb(43,43,43)",
                  primaryTextColor: "rgb(43,43,43)",
                  primaryColor: "rgb(43,43,43)",
                  titleColor: "rgb(43,43,43)",
                  alertColor: "rgb(43,43,43)",
                },
              }}
            >
              <Sensors />
              <CustomSensor />
              <Collection
                location={location}
                collection={{
                  ...page,
                  primaryCollection: shopifyCollection.primaryCollection,
                }}
                collectionGender={collectionGender}
              />
            </ReactifySearchProvider>
          </ProductImageProvider>
        </ReviewsProvider>
        <Script
          strategy="idle"
          src="https://widget.reviews.io/rating-snippet/dist.js"
        />
      </>
    );
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;

  return (
    <>
      <link
        rel="stylesheet"
        href="https://widget.reviews.io/rating-snippet/dist.css"
      />
      <MetaComponent
        meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
      />
    </>
  );
}
